import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import { REPORT_TEMPLATES, TEMPLATE_ROLES } from "./reportTemplates";
import {
  createStoreInfoLookup,
  groupStoreOptionsByCompany,
} from "../../../../helpers/storeOptions";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "98%",
  maxHeight: "96vh",
  overflow: "auto",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4
};

function TemplatePickerModal({
  open,
  onClose,
  onApplyTemplate,
  applyingTemplate,
  storeNames,
  selectedTab,
}) {
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedStores, setSelectedStores] = useState([]);
  const [storesExpanded, setStoresExpanded] = useState(false);

  // Initialize all stores as selected when modal opens
  useEffect(() => {
    if (open && Array.isArray(storeNames)) {
      setSelectedStores(
        storeNames
          .filter((s) => s && s.id != null)
          .map((s) => String(s.id))
      );
    }
  }, [open, storeNames]);

  const filteredTemplates = selectedRole
    ? REPORT_TEMPLATES.filter((t) => t.roles.includes(selectedRole))
    : REPORT_TEMPLATES;

  const handleClose = () => {
    if (applyingTemplate) return;
    setSelectedRole(null);
    setSelectedTemplate(null);
    onClose();
  };

  const handleApply = () => {
    if (!selectedTemplate || applyingTemplate || selectedStores.length === 0) return;
    onApplyTemplate(selectedTemplate, selectedStores, selectedTab);
  };

  const handleToggleStore = (storeId) => {
    setSelectedStores((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  const handleToggleAll = () => {
    if (!Array.isArray(storeNames)) return;
    const allIds = storeNames
      .filter((s) => s && s.id != null)
      .map((s) => String(s.id));

    if (selectedStores.length === allIds.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(allIds);
    }
  };

  const allStoreIds = Array.isArray(storeNames)
    ? storeNames.filter((s) => s && s.id != null).map((s) => String(s.id))
    : [];
  const allSelected = allStoreIds.length > 0 && selectedStores.length === allStoreIds.length;
  const someSelected = selectedStores.length > 0 && selectedStores.length < allStoreIds.length;

  const storeInfoLookup = React.useMemo(
    () => createStoreInfoLookup(storeNames),
    [storeNames]
  );

  const groupedStores = React.useMemo(() => {
    const storeOptions = (storeNames || [])
      .filter((s) => s && s.id != null)
      .map((s) => ({ store: s.id }));
    return groupStoreOptionsByCompany(storeOptions, storeInfoLookup);
  }, [storeNames, storeInfoLookup]);

  const showCompanyHeaders = groupedStores.length > 1;

  // Calculate total report count for a template based on selected stores
  const getTemplateReportCount = (template) => {
    const aggregateCount = template.reports.filter(
      (r) => r.scope !== "per_store"
    ).length;
    const perStoreCount = template.reports.filter(
      (r) => r.scope === "per_store"
    ).length;
    return aggregateCount + perStoreCount * selectedStores.length;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="template-picker-modal"
    >
      <Box sx={modalStyle}>
        {/* Close button */}
        <div className="absolute right-2 top-2">
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={applyingTemplate}
            className="text-gray-400 hover:text-gray-600"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Header */}
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-gray-900 mb-1">
            Dashboard Templates
          </h3>
          <p className="text-sm text-gray-500">
            Choose a template and select which stores to include in the generated reports.
          </p>
        </div>

        {/* Store selector */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setStoresExpanded((prev) => !prev)}
            className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2 hover:text-gray-900"
          >
            <svg
              className={`w-4 h-4 transition-transform ${storesExpanded ? "rotate-90" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Stores ({selectedStores.length} of {allStoreIds.length} selected)
          </button>
          {storesExpanded && (
          <div className="border border-gray-200 rounded-lg p-3 max-h-[200px] overflow-y-auto">
            {/* Select All */}
            <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleToggleAll}
                size="small"
                sx={{
                  color: "#6d28d9",
                  "&.Mui-checked": { color: "#6d28d9" },
                  "&.MuiCheckbox-indeterminate": { color: "#6d28d9" },
                  padding: "2px",
                }}
              />
              <span className="text-sm font-medium text-gray-700">
                {allSelected ? "Deselect All" : "Select All"}
              </span>
            </label>
            <div className="border-t border-gray-100 my-1"></div>
            {/* Stores grouped by company */}
            {groupedStores.map((group) => (
              <div key={group.companyId || group.companyName}>
                {showCompanyHeaders && (
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-2 mb-1 px-1">
                    {group.companyName}
                  </div>
                )}
                <div className="grid grid-cols-4 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2">
                  {group.options.map((option) => {
                    const storeId = String(option.store);
                    const storeInfo = storeInfoLookup.get(option.store);
                    const isChecked = selectedStores.includes(storeId);
                    return (
                      <label
                        key={storeId}
                        className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleStore(storeId)}
                          size="small"
                          sx={{
                            color: "#6d28d9",
                            "&.Mui-checked": { color: "#6d28d9" },
                            padding: "2px",
                          }}
                        />
                        <span className="text-sm text-gray-600 truncate">
                          {storeInfo?.store_name || `Store ${option.store}`}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Role filter chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => {
              setSelectedRole(null);
              setSelectedTemplate(null);
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedRole === null
                ? "bg-violet-700 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All
          </button>
          {TEMPLATE_ROLES.map((role) => (
            <button
              key={role.key}
              onClick={() => {
                setSelectedRole(role.key);
                setSelectedTemplate(null);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedRole === role.key
                  ? "bg-violet-700 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {role.label}
            </button>
          ))}
        </div>

        {/* Template cards */}
        <div className="max-h-[340px] overflow-y-auto space-y-3 pr-1">
          {filteredTemplates.map((template) => {
            const isSelected = selectedTemplate?.id === template.id;
            return (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(isSelected ? null : template)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? "border-violet-600 bg-violet-50"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      {template.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {template.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <span className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                      {getTemplateReportCount(template)} reports
                    </span>
                  </div>
                </div>

                {/* Role badges */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {template.roles.map((roleKey) => {
                    const role = TEMPLATE_ROLES.find((r) => r.key === roleKey);
                    return (
                      <span
                        key={roleKey}
                        className="px-2 py-0.5 bg-gray-50 text-gray-500 rounded text-xs"
                      >
                        {role?.label || roleKey}
                      </span>
                    );
                  })}
                </div>

                {/* Report list */}
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {template.reports.map((report, i) => (
                    <span key={i} className="text-xs text-gray-500">
                      {i + 1}. {report.name}
                      {report.scope === "per_store" && (
                        <span className="text-violet-500 ml-1">(per store)</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
          <button
            onClick={handleClose}
            disabled={applyingTemplate}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedTemplate || applyingTemplate || selectedStores.length === 0}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
              !selectedTemplate || applyingTemplate || selectedStores.length === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-violet-700 text-white hover:bg-violet-800"
            }`}
          >
            {applyingTemplate ? "Applying..." : "Apply Template"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default TemplatePickerModal;
